import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import { TRIAGEM_BTN, TRIAGEM_BTN_ATIVO, TRIAGEM_TH, TRIAGEM_TD } from '../triagem/triagemTabela';

interface Resolvido {
  razao_social: string; numero_pedido: string; atendimento_em: string; resolvido_em: string;
  validado_em: string | null; nota: number | null; configurou: string | null; horas: number | null;
}
interface ResumoAjudaBloco {
  pediram: number; configurados: number; validados: number; abertos: number;
  horas_configurar: number | null; horas_validar: number | null; horas_aberto_mais_antigo: number | null;
  resolvidos: Resolvido[];
}

const PERIODOS = [{ dias: 1, rotulo: 'Hoje' }, { dias: 7, rotulo: '7 dias' }, { dias: 30, rotulo: 'Mês' }] as const;
const horas = (h: number | null | undefined) => (h == null ? '—' : `${h.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`);
const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '');
const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

/**
 * O CONTROLE DA AJUDA DO BLOCO — o mesmo da validação do eSIM (Douglas
 * 25/09/2026): a Qualidade AJUDA a configurar o compartilhamento, RESOLVE e
 * VALIDA com o cliente — e a validação é a nota. Pedido com bloco não sai sem
 * ela. O relógio começa quando a ajuda começa.
 */
export function FaixaAjudaBloco({ segmento }: { segmento: string }) {
  const [dias, setDias] = useState<number>(30);
  const [verResolvidos, setVerResolvidos] = useState(false);
  const consulta = useQuery({ queryKey: ['triagem-bv-ajuda', segmento, dias],
    queryFn: () => requisitarAtivacao<ResumoAjudaBloco>(`boas-vindas/ajuda?segmento=${segmento}&dias=${dias}`), refetchInterval: 60_000 });
  const r = consulta.data;

  return <div className="border-b border-border/40 px-3 py-2">
    <div className="mb-1.5 flex items-center gap-2">
      <Share2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      <span className="text-[11px] font-semibold text-primary">Ajuda do compartilhamento</span>
      <span className="truncate text-[10px] text-muted-foreground">ajudar · configurar · validar com o cliente pela nota</span>
      <div className="ml-auto flex shrink-0 gap-1" role="group" aria-label="Período da ajuda">
        {PERIODOS.map(p => <button key={p.dias} type="button" aria-pressed={dias === p.dias} onClick={() => setDias(p.dias)}
          className={`${dias === p.dias ? TRIAGEM_BTN_ATIVO : TRIAGEM_BTN} !w-auto px-2 text-[10.5px]`}>{p.rotulo}</button>)}
      </div>
    </div>
    {consulta.error && <p role="alert" className="mb-1 text-[10.5px] text-destructive">{consulta.error.message}</p>}
    <div className="grid grid-cols-4 gap-1.5">
      <div className="rounded-md border border-border/60 px-2.5 py-1.5">
        <p className="text-[10px] text-muted-foreground">Precisaram de ajuda {dias === 1 ? 'hoje' : dias === 7 ? 'nos 7 dias' : 'no mês'}</p>
        <p className="font-display text-[16px] font-bold tabular-nums">{r?.pediram ?? '—'}</p>
      </div>
      <div className="rounded-md border border-border/60 px-2.5 py-1.5">
        <p className="text-[10px] text-muted-foreground">Configurados · em {horas(r?.horas_configurar)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums">{r?.configurados ?? '—'} <span className="text-[11px] font-normal text-muted-foreground">{pct(r?.configurados || 0, r?.pediram || 0)}</span></p>
      </div>
      <button type="button" onClick={() => setVerResolvidos(true)} title="Ver quem já foi configurado e validado"
        className="rounded-md border border-border/60 px-2.5 py-1.5 text-left hover:border-[#075E54]">
        <p className="text-[10px] text-[#075E54] dark:text-[#25D366]">Cliente validou com a nota · em {horas(r?.horas_validar)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums text-[#075E54] dark:text-[#25D366]">{r?.validados ?? '—'} <span className="text-[11px] font-normal">{pct(r?.validados || 0, r?.pediram || 0)}</span></p>
      </button>
      <div className="rounded-md border border-primary/60 bg-primary/5 px-2.5 py-1.5">
        <p className="text-[10px] text-primary">Em aberto · mais antigo {horas(r?.horas_aberto_mais_antigo)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums text-primary">{r?.abertos ?? '—'}</p>
      </div>
    </div>
    <p className="mt-1 text-[10px] text-muted-foreground">Sem nota o pedido com bloco não sai ·{' '}
      <button type="button" className="underline hover:text-primary" onClick={() => setVerResolvidos(true)}>ver configurados ({r?.resolvidos?.length ?? 0})</button></p>

    <Dialog open={verResolvidos} onOpenChange={setVerResolvidos}><DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>Configurados · {PERIODOS.find(p => p.dias === dias)?.rotulo}</DialogTitle></DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto">
        {r?.resolvidos?.length
          ? <table className="w-full table-fixed"><thead className="sticky top-0"><tr>
              <th className={TRIAGEM_TH}>Cliente · pedido</th><th className={`${TRIAGEM_TH} w-[110px]`}>Quem configurou</th>
              <th className={`${TRIAGEM_TH} w-[92px]`}>Ajuda desde</th><th className={`${TRIAGEM_TH} w-[92px]`}>Configurou</th>
              <th className={`${TRIAGEM_TH} w-[92px]`}>Validou</th><th className={`${TRIAGEM_TH} w-[60px]`}>Tempo</th>
            </tr></thead><tbody>
              {r.resolvidos.map((x, i) => <tr key={i} className="border-t border-border/40">
                <td className={TRIAGEM_TD}><span className="block truncate text-[11px] font-semibold" title={x.razao_social}>{x.razao_social}</span><span className="block text-[9px] text-muted-foreground">{x.numero_pedido}</span></td>
                <td className={`${TRIAGEM_TD} truncate text-[11px]`}>{x.configurou || '—'}</td>
                <td className={`${TRIAGEM_TD} text-[11px]`}>{quando(x.atendimento_em)}</td>
                <td className={`${TRIAGEM_TD} text-[11px]`}>{quando(x.resolvido_em)}</td>
                <td className={`${TRIAGEM_TD} text-[11px] ${x.validado_em ? 'text-[#075E54] dark:text-[#25D366]' : 'text-muted-foreground'}`}>{x.validado_em ? `${quando(x.validado_em)} · nota ${x.nota}` : 'esperando a nota'}</td>
                <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{horas(x.horas)}</td>
              </tr>)}
            </tbody></table>
          : <p className="p-4 text-center text-[11px] text-muted-foreground">Nenhum compartilhamento configurado neste período.</p>}
      </div>
    </DialogContent></Dialog>
  </div>;
}
